import { StatusDot, type StatusKey } from "@/components/StatusIndicator";
import type { CSSProperties, ReactNode } from "react";

/**
 * KpiCard - 계획서 4-1 의 상단 카운터 6개
 *   전체 개발품 / 정상 / 일정주의 / 지연 / 금주 생산 / SOP 임박
 *
 * 문제: 지연과 SOP 임박이 먼저 눈에 들어와야 하는데, 지연이 0 일
 * 때는 페이지가 소리를 지르면 안 된다.
 *
 * 해결: 4단 위계를 색이 아니라 크기/무게/규칙선/글리프로 만든다.
 *
 *   anchor  전체    28px 700  중립잉크  선 없음  글리프 없음
 *   alarm   지연    28px 700  상태색    3px 선   글리프 + 틴트
 *   attend  SOP임박 28px 700  상태색    3px 선   글리프
 *   inform  나머지  22px 600  중립잉크  선 없음  글리프 없음
 *
 * 색을 다 지워도 위계가 남는다 (크기 2단 + 규칙선 유무 + 글리프
 * 유무 + 틴트 유무).
 *
 * 0 붕괴: alarm/attend 인데 값이 0 이면 스스로 inform 으로
 * 내려앉는다. 잉크는 tertiary, 선과 틴트는 사라지고, 라벨 옆에
 * 완료 체크가 붙는다. 지연이 없는 날의 대시보드는 조용하다.
 * 이 규칙을 컴포넌트 안에 넣어둔 이유는, 화면 쪽에서 잊어버리면
 * 0 건인데도 빨간 카드가 떠 있게 되기 때문이다.
 */

export type KpiEmphasis = "anchor" | "alarm" | "attend" | "inform";

export const KpiCard = ({
  label,
  value,
  unit = "건",
  emphasis = "inform",
  state,
  active = false,
  onClick,
  hint,
  hero = false,
}: {
  label: string;
  value: number;
  /** 건, 개 … 없애려면 빈 문자열 */
  unit?: string;
  emphasis?: KpiEmphasis;
  /** alarm/attend 가 쓰는 상태색. alarm 은 late, attend 는 warn. */
  state?: StatusKey;
  /** 이 카드가 곧 필터일 때, 현재 적용 중인지 */
  active?: boolean;
  /** 누르면 목록이 이 조건으로 걸러진다 (계획서 4 의 상태 필터) */
  onClick?: () => void;
  hint?: string;
  /**
   * 이 카드 하나만 44px 로 올린다. 대시보드에서 제일 먼저 읽혀야 할 숫자가
   * 나머지 7개와 같은 급이면 위계가 없는 것과 같다 (§4-1 리디자인).
   * 0 붕괴는 그대로 적용된다 - 지연이 0 인 날은 크게 외치지 않는다.
   */
  hero?: boolean;
}) => {
  // 0 붕괴.
  const quiet = (emphasis === "alarm" || emphasis === "attend") && value === 0;
  const eff: KpiEmphasis = quiet ? "inform" : emphasis;
  const big = eff !== "inform";
  // 0 으로 붕괴하면 히어로도 함께 내려온다 - 조용한 날의 대시보드가
  // 44px 로 "0" 을 외치면 그게 제일 시끄럽다.
  const huge = hero && !quiet;
  const color = state && !quiet ? `var(--state-${state})` : undefined;

  const ink =
    eff === "alarm" || eff === "attend"
      ? (color ?? "var(--text-primary)")
      : quiet
        ? "var(--text-tertiary)"
        : "var(--text-primary)";

  const body = (
    <>
      {/* 3px 규칙선. 색맹에게도 "이 카드는 다르다"를 전달한다. */}
      {big && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 h-full w-[3px]"
          style={{ background: color ?? "var(--accent)" }}
        />
      )}

      <span
        className="flex items-center gap-s1 text-caption"
        style={{ color: "var(--text-secondary)" }}
      >
        {label}
        {big && state && <StatusDot status={state} size="row" labelled />}
        {quiet && <StatusDot status="done" size="row" labelled />}
      </span>

      <span className="flex items-baseline gap-s1">
        <span
          className={[
            "tabular-nums",
            huge ? "text-kpi-hero font-bold" : big ? "text-kpi font-bold" : "text-title font-semibold",
          ].join(" ")}
          style={{ color: ink }}
        >
          {value}
        </span>
        {unit && (
          <span className="text-caption" style={{ color: "var(--text-tertiary)" }}>
            {unit}
          </span>
        )}
      </span>

      {hint && (
        <span className="text-micro" style={{ color: "var(--text-tertiary)" }}>
          {hint}
        </span>
      )}
    </>
  );

  const base =
    "relative flex flex-col items-start gap-s1 overflow-hidden rounded-card text-left px-s5 py-s4 transition-colors duration-fast ease-standard";
  const shell: CSSProperties = {
    background:
      eff === "alarm" && state && !quiet
        ? `var(--state-${state}-soft)`
        : active
          ? "var(--accent-soft)"
          : "var(--surface)",
    boxShadow: active ? "inset 0 0 0 1px var(--accent)" : "var(--shadow-raised)",
  };

  if (!onClick) {
    return (
      <div className={base} style={shell}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`${base} cursor-pointer hover:bg-overlay-hover active:bg-overlay-press`}
      style={shell}
    >
      {body}
    </button>
  );
};

/**
 * 보조 KPI 줄. 데스크톱 5열 -> 태블릿 3열 -> 모바일 2열.
 *
 * 계획서 순서(전체/정상/일정주의/지연/금주생산/SOP임박)에서 **지연 ·
 * SOP 임박 · 전체** 셋은 위 히어로 줄로 올라갔다. 순서를 흔드는 대신
 * 위계를 만든 것이다 - 8개가 같은 무게로 깔리면 값이 0 인 칸이 제일
 * 넓은 자리를 차지한다 (§4-1 리디자인).
 */
export const KpiHeader = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-2 gap-s3 sm:grid-cols-3 lg:grid-cols-5">{children}</div>
);
